/**
 * Branding context — fetches `tenant.branding.get` via SWR and shares the
 * result with the Shell header (logo lockups) and the Settings page
 * (form prefill + save). Settings calls `refresh()` after writes; that
 * resolves to a `mutate()` on the SWR cache so any other consumers
 * pick up the new branding immediately.
 *
 * Branding is tenant-wide, not group-scoped — the cache key embeds a
 * literal `null` for the group segment to match the shape produced by
 * `useTenantQuery(..., { ignoreActiveGroup: true })`. This means
 * `useInvalidate('branding.get')` from any page also revalidates here.
 *
 * While the fetch is in flight, `branding` is seeded from the pre-mount
 * owner branding injected by index.html's blocking bootstrap script
 * (see `owner-branding.ts` / REGTA-74) instead of `null`, so the shell's
 * first paint already shows the owner's colors and logos. Only the
 * tenant-level overrides (rarely set) arrive with the fetch.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import useSWR from 'swr';
import { OwnerBrandingProvider, type OwnerBrandingValue } from '@hyranai/regta-templates';
import { tenantApi, type BrandingResponse } from './api.js';
import { useTenantSession } from './auth-context.js';
import { ownerPublicBranding, REGTA_PRIMARY } from './owner-branding.js';

// The blocking bootstrap script in index.html runs before any module
// code, so the injected global is safe to read at module scope.
function bootBranding(): BrandingResponse {
  const boot = ownerPublicBranding();
  return {
    owner: {
      ownerId: '',
      name: boot.ownerName,
      slug: boot.ownerSlug,
      brandLogoOnWhiteUrl: boot.logoOnWhiteUrl,
      brandLogoOnPrimaryUrl: boot.logoOnPrimaryUrl,
      brandColors: boot.primaryColor ? { primary: boot.primaryColor } : null,
    },
    tenant: {
      tenantId: '',
      name: null,
      brandLogoOnWhiteUrl: null,
      brandLogoOnPrimaryUrl: null,
      brandColors: null,
    },
  };
}

const BOOT_BRANDING = bootBranding();

type Ctx = {
  branding: BrandingResponse | null;
  error: string | null;
  refresh: () => Promise<void>;
};

const BrandingCtx = createContext<Ctx>({
  branding: null,
  error: null,
  refresh: async () => {},
});

export function useBranding(): Ctx {
  return useContext(BrandingCtx);
}

/**
 * Owner brand primary with the Regta-green fallback — the single
 * resolver for every SPA surface that paints owner chrome (sidebar,
 * Översikt cards). Duplicating the `?? fallback` expression per file is
 * how chrome surfaces drift apart.
 */
export function useBrandPrimary(): string {
  const { branding } = useBranding();
  return branding?.owner.brandColors?.primary ?? REGTA_PRIMARY;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { isReady, userId } = useTenantSession();
  const { data, error, mutate } = useSWR(
    isReady && userId ? (['branding.get', userId, null] as const) : null,
    async () => tenantApi.branding.get(),
    { fallbackData: BOOT_BRANDING },
  );
  const branding: BrandingResponse | null = data ?? null;

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const ownerValue = useMemo<OwnerBrandingValue | null>(() => {
    if (!branding) return null;
    // Templates render on white paper, so OwnerLogo always gets the
    // on-white variant. The on-primary variant is consumed by the SPA
    // chrome (side menu), not by template-side renderers.
    return {
      logoUrl: branding.owner.brandLogoOnWhiteUrl,
      name: branding.owner.name,
      tenantLogoUrl: branding.tenant.brandLogoOnWhiteUrl,
      tenantName: branding.tenant.name ?? null,
    };
  }, [branding]);

  return (
    <BrandingCtx.Provider
      value={{ branding, error: error ? (error as Error).message : null, refresh }}
    >
      <OwnerBrandingProvider value={ownerValue}>{children}</OwnerBrandingProvider>
    </BrandingCtx.Provider>
  );
}
